import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "./auth";

function buildAuthHeaders(token) {
  const clean = typeof token === "string" ? token.trim() : "";
  const isJwt = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(clean);
  return isJwt ? { Authorization: `Bearer ${clean}` } : {};
}

const VERDICT_COLOR = {
  WORKS: "#000",
  ALMOST: "#8a7a5a",
  REWORK: "#c0392b",
};

export default function AdminPage() {
  const { user, token, loading: authLoading, signInWithGoogle } = useAuth();
  const [stats, setStats]       = useState(null);
  const [users, setUsers]       = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [flags, setFlags]       = useState([]);
  const [anchorProducts, setAnchorProducts] = useState([]);
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    setLoading(true); setError(null);
    const headers = { ...buildAuthHeaders(token), "Content-Type": "application/json" };
    Promise.all([
      fetch("/api/admin/stats", { headers, credentials: "include" }).then(r => r.json()),
      fetch("/api/admin/users", { headers, credentials: "include" }).then(r => r.json()),
      fetch("/api/admin/analyses?limit=50", { headers, credentials: "include" }).then(r => r.json()),
      fetch("/api/admin/flags", { headers, credentials: "include" }).then(r => r.json()),
      fetch("/api/admin/anchor-products", { headers, credentials: "include" }).then(r => r.json()),
    ])
      .then(([s, u, a, f, p]) => {
        if (s.error) return setError(s.error);
        setStats(s);
        setUsers(u.users || []);
        setAnalyses(a.analyses || []);
        setFlags(f.flags || []);
        setAnchorProducts(p.products || []);
      })
      .catch(err => setError(err.message || "Failed to load admin data"))
      .finally(() => setLoading(false));
  }, [user, token, authLoading]);

  if (authLoading) return <div className="admin-loading">Loading…</div>;
  if (!user) {
    return (
      <div className="admin-gate">
        <h1 className="admin-title">Admin</h1>
        <p className="admin-sub">Sign in required.</p>
        <button className="auth-btn auth-btn-google" onClick={() => signInWithGoogle()}>
          Continue with Google
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-gate">
        <h1 className="admin-title">Admin</h1>
        <p className="admin-sub">{error}</p>
        <Link to="/" className="admin-back">← back to app</Link>
      </div>
    );
  }

  return (
    <div className="admin">
      <header className="admin-header">
        <div>
          <h1 className="admin-title">Admin</h1>
          <p className="admin-sub">Signed in as {user.email}</p>
        </div>
        <Link to="/" className="admin-back">← back to app</Link>
      </header>

      {loading && <div className="admin-loading">Loading data…</div>}

      {stats && (
        <>
          {stats.credits && (
            <div className="admin-stats admin-stats-prominent">
              <StatCard
                label="Anthropic balance remaining"
                value={formatUSD(stats.credits.balance_usd)}
                hint={`$${stats.credits.budget_usd.toFixed(2)} budget − ${formatUSD(stats.credits.spent_usd)} spent`}
                emphasis
              />
              <a
                className="stat-card stat-card-link"
                href="https://console.anthropic.com/settings/billing"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="stat-value-link">View on Anthropic →</div>
                <div className="stat-label">Authoritative balance + top up</div>
              </a>
            </div>
          )}

          <div className="admin-stats">
            <StatCard label="Users" value={stats.users_total} />
            <StatCard label="Analyses" value={stats.analyses_total} />
            <StatCard label="Analyses · last 24h" value={stats.analyses_last_24h} />
          </div>

          {stats.tokens && (
            <div className="admin-stats">
              <StatCard
                label="Tokens · all time"
                value={`${formatK(stats.tokens.input_all)} in · ${formatK(stats.tokens.output_all)} out`}
              />
              <StatCard
                label="Tokens · last 24h"
                value={`${formatK(stats.tokens.input_24h)} in · ${formatK(stats.tokens.output_24h)} out`}
              />
              <StatCard
                label="Est. spend · all time"
                value={formatUSD(stats.tokens.est_cost_all_usd)}
                hint={`$${stats.tokens.price_per_mtok.input}/M in · $${stats.tokens.price_per_mtok.output}/M out`}
              />
              <StatCard
                label="Est. spend · last 24h"
                value={formatUSD(stats.tokens.est_cost_24h_usd)}
              />
            </div>
          )}
        </>
      )}

      {users.length > 0 && (
        <section className="admin-section">
          <h2 className="admin-section-title">Users ({users.length})</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Signed up</th>
                <th>Last seen</th>
                <th style={{ textAlign: "right" }}>Credits</th>
                <th style={{ textAlign: "right" }}>Analyses</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{formatDate(u.created_at)}</td>
                  <td>{formatDate(u.last_sign_in_at)}</td>
                  <td style={{ textAlign: "right" }}>{u.credits ?? "—"}</td>
                  <td style={{ textAlign: "right" }}>{u.analyses_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <FlagsSection
        flags={flags}
        onChange={setFlags}
        token={token}
      />

      <AnchorProductsSection
        products={anchorProducts}
        onChange={setAnchorProducts}
        token={token}
      />

      {analyses.length > 0 && (
        <section className="admin-section">
          <h2 className="admin-section-title">Recent analyses ({analyses.length})</h2>
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Tone</th>
                <th>Occasion</th>
                <th>Verdict</th>
                <th style={{ textAlign: "right" }}>Score</th>
                <th style={{ textAlign: "right" }}>Tokens</th>
                <th>Public</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              {analyses.map(a => (
                <tr key={a.id}>
                  <td>{formatDate(a.created_at)}</td>
                  <td>{a.user_email || <span style={{ color: "#666" }}>anon</span>}</td>
                  <td>{a.tone}</td>
                  <td>{a.occasion}</td>
                  <td style={{ color: VERDICT_COLOR[a.verdict] || "#000", fontWeight: 500 }}>
                    {a.verdict}
                  </td>
                  <td style={{ textAlign: "right" }}>{a.score}</td>
                  <td style={{ textAlign: "right", color: "#444", fontSize: "0.7rem" }}>
                    {a.input_tokens != null
                      ? `${formatK(a.input_tokens)} / ${formatK(a.output_tokens || 0)}`
                      : "—"}
                  </td>
                  <td>{a.is_public ? "yes" : "no"}</td>
                  <td>
                    <Link to={`/result/${a.id}`} target="_blank" rel="noopener noreferrer">
                      /result/{a.id.slice(0, 8)}…
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function FlagsSection({ flags, onChange, token }) {
  const [newKey, setNewKey] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [err, setErr] = useState(null);

  const upsert = async (key, enabled, description) => {
    setBusyKey(key); setErr(null);
    try {
      const headers = { ...buildAuthHeaders(token), "Content-Type": "application/json" };
      const body = { enabled };
      if (description !== undefined) body.description = description;
      const res = await fetch(`/api/admin/flags/${encodeURIComponent(key)}`, {
        method: "PUT", headers, credentials: "include", body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      const next = flags.filter(f => f.key !== key).concat(data.flag).sort((a, b) => a.key.localeCompare(b.key));
      onChange(next);
      return true;
    } catch (e) {
      setErr(e.message || "Failed");
      return false;
    } finally {
      setBusyKey(null);
    }
  };

  const addNew = async (e) => {
    e.preventDefault();
    const key = newKey.trim();
    if (!key) return;
    const ok = await upsert(key, false, newDesc.trim() || null);
    if (ok) { setNewKey(""); setNewDesc(""); }
  };

  return (
    <section className="admin-section">
      <h2 className="admin-section-title">Feature flags ({flags.length})</h2>
      {flags.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>State</th>
              <th>Description</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {flags.map(f => (
              <tr key={f.key}>
                <td><code>{f.key}</code></td>
                <td>
                  <button
                    className={`flag-toggle${f.enabled ? " is-on" : ""}`}
                    disabled={busyKey === f.key}
                    onClick={() => upsert(f.key, !f.enabled)}
                  >
                    {f.enabled ? "ON" : "OFF"}
                  </button>
                </td>
                <td style={{ color: f.description ? "#000" : "#aaa", fontStyle: f.description ? "normal" : "italic" }}>
                  {f.description || "—"}
                </td>
                <td style={{ color: "#444" }}>{formatDate(f.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form onSubmit={addNew} className="flag-add-row">
        <input
          className="flag-add-input"
          placeholder="new_flag_key"
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          pattern="[A-Za-z0-9_]{1,64}"
          title="letters, digits, underscores (1-64 chars)"
        />
        <input
          className="flag-add-input flag-add-desc"
          placeholder="description (optional)"
          value={newDesc}
          onChange={e => setNewDesc(e.target.value)}
        />
        <button className="flag-add-btn" type="submit" disabled={!newKey.trim() || busyKey !== null}>Add</button>
      </form>
      {err && <div className="admin-error">{err}</div>}
    </section>
  );
}

const EMPTY_ANCHOR = {
  brand: "", category: "", gender: "unisex", name: "",
  price_cents: "", price_tier: "$$", image_url: "", affiliate_url: "",
  tags: "", notes: "",
};

function AnchorProductsSection({ products, onChange, token }) {
  const [draft, setDraft] = useState(EMPTY_ANCHOR);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [filterBrand, setFilterBrand] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  const brands = [...new Set(products.map(p => p.brand))].sort();
  const categories = [...new Set(products.map(p => p.category))].sort();
  const visible = products.filter(p =>
    (!filterBrand || p.brand === filterBrand) &&
    (!filterCategory || p.category === filterCategory)
  );

  const headers = () => ({ ...buildAuthHeaders(token), "Content-Type": "application/json" });

  const payloadFromDraft = (d) => ({
    brand: d.brand.trim(),
    category: d.category.trim().toLowerCase(),
    gender: d.gender,
    name: d.name.trim(),
    price_cents: parseInt(d.price_cents, 10),
    price_tier: d.price_tier,
    image_url: d.image_url.trim(),
    affiliate_url: d.affiliate_url.trim(),
    tags: d.tags.split(",").map(s => s.trim()).filter(Boolean),
    notes: d.notes.trim() || null,
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const body = payloadFromDraft(draft);
      const url = editingId
        ? `/api/admin/anchor-products/${editingId}`
        : "/api/admin/anchor-products";
      const method = editingId ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: headers(), credentials: "include", body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      const next = editingId
        ? products.map(p => p.id === editingId ? data.product : p)
        : [...products, data.product];
      onChange(next.sort((a, b) => a.brand.localeCompare(b.brand) || a.category.localeCompare(b.category) || a.name.localeCompare(b.name)));
      setDraft(EMPTY_ANCHOR); setEditingId(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (p) => {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`/api/admin/anchor-products/${p.id}`, {
        method: "PUT", headers: headers(), credentials: "include",
        body: JSON.stringify({ active: !p.active }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onChange(products.map(x => x.id === p.id ? data.product : x));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setDraft({
      brand: p.brand, category: p.category, gender: p.gender, name: p.name,
      price_cents: String(p.price_cents), price_tier: p.price_tier,
      image_url: p.image_url, affiliate_url: p.affiliate_url,
      tags: (p.tags || []).join(", "), notes: p.notes || "",
    });
    if (typeof window !== "undefined") window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setDraft(EMPTY_ANCHOR); };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.brand} ${p.name}"?`)) return;
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`/api/admin/anchor-products/${p.id}`, {
        method: "DELETE", headers: headers(), credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed");
      }
      onChange(products.filter(x => x.id !== p.id));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  return (
    <section className="admin-section">
      <h2 className="admin-section-title">Anchor products ({products.length})</h2>

      {products.length > 0 && (brands.length > 1 || categories.length > 1) && (
        <div className="anchor-filters">
          <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)} className="anchor-filter">
            <option value="">All brands</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="anchor-filter">
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="anchor-filter-count">{visible.length} shown</span>
        </div>
      )}

      {visible.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Img</th>
              <th>Brand</th>
              <th>Category</th>
              <th>Name</th>
              <th>Price</th>
              <th>Gender</th>
              <th>Tags</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(p => (
              <tr key={p.id} style={{ opacity: p.active ? 1 : 0.45 }}>
                <td>
                  {p.image_url && <img src={p.image_url} alt="" className="anchor-thumb" />}
                </td>
                <td>{p.brand}</td>
                <td><code>{p.category}</code></td>
                <td>
                  <a href={p.affiliate_url} target="_blank" rel="noopener noreferrer">{p.name}</a>
                </td>
                <td>${(p.price_cents / 100).toFixed(0)} <span style={{ color: "#666" }}>{p.price_tier}</span></td>
                <td>{p.gender}</td>
                <td style={{ fontSize: "0.7rem", color: "#444" }}>{(p.tags || []).join(", ")}</td>
                <td>
                  <button className={`flag-toggle${p.active ? " is-on" : ""}`} onClick={() => toggleActive(p)} disabled={busy}>
                    {p.active ? "ON" : "OFF"}
                  </button>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="anchor-action-btn" onClick={() => startEdit(p)} disabled={busy}>Edit</button>
                  <button className="anchor-action-btn anchor-action-danger" onClick={() => remove(p)} disabled={busy}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form onSubmit={submit} className="anchor-form">
        <h3 className="anchor-form-title">{editingId ? "Edit product" : "Add product"}</h3>
        <div className="anchor-form-grid">
          <label>Brand
            <input value={draft.brand} onChange={set("brand")} placeholder="J.Crew" required />
          </label>
          <label>Category
            <input value={draft.category} onChange={set("category")} placeholder="henley" required />
          </label>
          <label>Gender
            <select value={draft.gender} onChange={set("gender")}>
              <option value="men">men</option>
              <option value="women">women</option>
              <option value="unisex">unisex</option>
            </select>
          </label>
          <label>Price tier
            <select value={draft.price_tier} onChange={set("price_tier")}>
              <option>$</option><option>$$</option><option>$$$</option>
            </select>
          </label>
          <label style={{ gridColumn: "span 2" }}>Product name
            <input value={draft.name} onChange={set("name")} placeholder="Slim Slub Henley" required />
          </label>
          <label>Price (cents)
            <input value={draft.price_cents} onChange={set("price_cents")} type="number" min="0" placeholder="5800" required />
          </label>
          <label>Tags (comma sep)
            <input value={draft.tags} onChange={set("tags")} placeholder="cotton, slim, navy" />
          </label>
          <label style={{ gridColumn: "span 2" }}>Image URL
            <input value={draft.image_url} onChange={set("image_url")} placeholder="https://..." type="url" required />
          </label>
          <label style={{ gridColumn: "span 2" }}>Affiliate URL
            <input value={draft.affiliate_url} onChange={set("affiliate_url")} placeholder="https://..." type="url" required />
          </label>
          <label style={{ gridColumn: "span 2" }}>Notes
            <input value={draft.notes} onChange={set("notes")} placeholder="optional" />
          </label>
        </div>
        {draft.image_url && (
          <div className="anchor-preview">
            <img src={draft.image_url} alt="" className="anchor-thumb-lg" />
          </div>
        )}
        <div className="anchor-form-actions">
          <button type="submit" className="flag-add-btn" disabled={busy}>
            {editingId ? "Save changes" : "Add product"}
          </button>
          {editingId && <button type="button" className="anchor-action-btn" onClick={cancelEdit}>Cancel</button>}
        </div>
      </form>
      {err && <div className="admin-error">{err}</div>}
    </section>
  );
}

function StatCard({ label, value, hint, emphasis }) {
  return (
    <div className={`stat-card${emphasis ? " stat-card-emphasis" : ""}`}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  );
}

function formatK(n) {
  if (!n && n !== 0) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatUSD(n) {
  if (!n && n !== 0) return "$0.00";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
